import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StorageIcon from '@mui/icons-material/Storage';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import GavelIcon from '@mui/icons-material/Gavel';
import CancelIcon from '@mui/icons-material/Cancel';
import Markdown from 'markdown-to-jsx';
import { useConfirm } from 'material-ui-confirm';
import { useApiContext } from '@/contexts/apiContext';
import ErrorMessage from '@/components/errorMessage';
import { EntityType } from '@/components/entities/entityType';
import { EventPayloadViewer, JsonViewer } from '@/components/viewers';
import {
  ExecutionStep,
  ExecutionStepResult,
  ItemProcessingStatusDto,
  PipelineExecutionState,
} from '@/interfaces/pipelineExecution.interface';
import { formatRunDuration } from '@/utils';

interface AgentStepItemsViewProps {
  step: ExecutionStep;
  pipelineExecutionState?: PipelineExecutionState;
  onRestartFromPoint?: (step: ExecutionStep, eventDataId?: string | number) => void;
  onExecuteStep?: (
    step: any,
    options?: { inputOverride?: any; reingest?: boolean; suppressEvents?: boolean },
  ) => Promise<void> | void;
  suppressEvents?: boolean;
  onToggleSuppressEvents?: (suppress: boolean) => void;
  stepExecutionResponse?: ExecutionStepResult | null;
  onClearStepExecutionResponse?: () => void;
  onChange?: () => void;
  isExecuting?: boolean;
}

const formatBytes = (bytes?: number): string => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/**
 * Component for an Individual Agent Event Data Record
 */
const AgentEventCard: React.FC<{
  ev: any;
  onDelete: (ev: any) => void;
}> = ({ ev, onDelete }) => {
  const [showInputContext, setShowInputContext] = useState(false);
  const [copiedId, setCopiedId] = useState<string | number | null>(null);

  const payload = ev.eventPayload ?? ev.result ?? ev;
  const isEvFailed = Boolean(ev.error);

  const handleCopy = (text: string, id: string | number) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1,
        borderRadius: 1,
        bgcolor: isEvFailed ? '#fff5f5' : '#fafafa',
        border: isEvFailed ? '1px solid #fecaca' : '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
      }}
    >
      <Stack spacing={0.75} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
          sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
            <Chip
              label={isEvFailed ? 'FAILED' : ev.eventName || 'PROCESSED'}
              size="small"
              color={isEvFailed ? 'error' : 'primary'}
              variant="filled"
              sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, flexShrink: 0 }}
            />
            {ev.id && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', flexShrink: 0 }}>
                Event ID: <code>{ev.id}</code>
              </Typography>
            )}
            {ev.dateTime && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.68rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                • {new Date(ev.dateTime).toLocaleString()}
              </Typography>
            )}
          </Stack>

          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0, ml: 'auto' }}>
            <Tooltip title={copiedId === ev.id ? 'Copied!' : 'Copy Payload JSON'}>
              <IconButton
                size="small"
                onClick={() => handleCopy(JSON.stringify(payload, null, 2), ev.id)}
                sx={{ p: 0.25 }}
              >
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete this event record">
              <IconButton size="small" color="error" onClick={() => onDelete(ev)} sx={{ p: 0.25 }}>
                <DeleteOutlineIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* PRIMARY: Event Output Payload */}
        <EventPayloadViewer payload={ev.eventPayload ?? ev.result ?? ev} label="Output Payload" showMimeType={true} />

        {/* SECONDARY: Inbound Input Context (Collapsible) */}
        {ev.inputEvent && (
          <Box
            sx={{ width: '100%', maxWidth: '100%', border: '1px solid #f1f5f9', borderRadius: 1, bgcolor: '#ffffff' }}
          >
            <Box
              onClick={() => setShowInputContext(!showInputContext)}
              sx={{
                px: 1,
                py: 0.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                userSelect: 'none',
                '&:hover': { bgcolor: '#f1f5f9' },
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.7rem' }}>
                Input Context ({ev.inputEvent.eventName || 'Event'})
              </Typography>
              <IconButton size="small" sx={{ p: 0.25 }}>
                {showInputContext ? (
                  <KeyboardArrowUpIcon sx={{ fontSize: 14 }} />
                ) : (
                  <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                )}
              </IconButton>
            </Box>

            <Collapse in={showInputContext}>
              <Divider sx={{ borderColor: '#f1f5f9' }} />
              <Box sx={{ p: 1 }}>
                <EventPayloadViewer
                  payload={ev.inputEvent.eventPayload ?? ev.inputEvent}
                  label="Inbound Input Context"
                  showMimeType={false}
                />
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Error Banner if event failed */}
        {ev.error && <ErrorMessage error={ev.error} />}
      </Stack>
    </Paper>
  );
};

/**
 * Expandable Row for an Agent-processed item
 */
const AgentStepItemRow: React.FC<{
  step: ExecutionStep;
  item: ItemProcessingStatusDto;
  pipelineInstanceId?: string;
  onRestartFromPoint?: (step: ExecutionStep, eventDataId?: string | number) => void;
  onItemDeleted?: () => void;
  onChange?: () => void;
}> = ({ step, item, pipelineInstanceId, onRestartFromPoint, onItemDeleted, onChange }) => {
  const apiContext = useApiContext();
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [eventsData, setEventsData] = useState<any[]>([]);
  const [isResumingItem, setIsResumingItem] = useState(false);

  // Deletion error states
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteItemError, setDeleteItemError] = useState<string | null>(null);

  const handleDeleteItem = async () => {
    if (!pipelineInstanceId) return;
    try {
      await confirm({
        title: 'Reset Item from Agent',
        description: `Are you sure you want to delete the processing records for "${item.itemName || `Item #${item.dataSourceItemId || item.id}`}"? This will remove the processing status and event data for this agent and all subsequent downstream agents in the pipeline. The original item in the data source will remain intact.`,
        confirmationText: 'Reset Item',
        cancellationText: 'Cancel',
        confirmationButtonProps: { color: 'error', variant: 'contained' },
      });
      setDeleteItemError(null);
      await apiContext.deleteAgentInstanceItemProcessingStatus(
        step.entityDefId,
        step.entityId,
        item.id,
        pipelineInstanceId,
      );
      if (onItemDeleted) onItemDeleted();
      if (onChange) onChange();
    } catch (err: any) {
      if (err) {
        console.error('Error deleting item from pipeline:', err);
        setDeleteItemError(err?.response?.data?.message || err?.message || 'Failed to reset item from pipeline.');
      }
    }
  };

  const fetchEvents = async () => {
    setLoadingEvents(true);
    try {
      const data = await apiContext.getAgentInstanceEventsData(
        step.entityDefId,
        step.entityId,
        item.dataSourceDefId,
        item.dataSourceInstanceId,
        String(item.dataSourceItemId || item.dataSourceItemGroup || item.id),
        pipelineInstanceId,
      );
      setEventsData(data || []);
    } catch (err) {
      console.error('Error fetching agent item events data:', err);
    } finally {
      setLoadingEvents(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchEvents();
    }
  }, [open]);

  const handleDeleteEvent = async (ev: any) => {
    if (!ev?.id) return;
    try {
      await confirm({
        title: 'Delete Event Data',
        description: `Are you sure you want to delete event record "${ev.id}"? This will permanently remove this generated event from the database.`,
        confirmationText: 'Delete',
        cancellationText: 'Cancel',
        confirmationButtonProps: { color: 'error', variant: 'contained' },
      });
      setDeleteError(null);
      await apiContext.deleteAgentInstanceEventData(step.entityDefId, step.entityId, ev.id);
      const updatedEvents = (eventsData || []).filter((e) => e.id !== ev.id);
      setEventsData(updatedEvents);
      if (updatedEvents.length === 0 && onItemDeleted) {
        onItemDeleted();
      } else {
        await fetchEvents();
      }
      if (onChange) onChange();
    } catch (err: any) {
      if (err) {
        console.error('Error deleting event data:', err);
        setDeleteError(err?.response?.data?.message || err?.message || 'Failed to delete event data.');
      }
    }
  };

  const isFailed =
    item.status?.toLowerCase() === 'failed' ||
    item.status?.toLowerCase() === 'error' ||
    Boolean(item.error) ||
    Boolean(item.dsError);

  const duration = formatRunDuration(item.lastProcessedStartDate, item.lastProcessedEndDate);

  return (
    <>
      <TableRow hover sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell sx={{ width: 36, py: 0.5, px: 1 }}>
          <IconButton size="small" onClick={() => setOpen(!open)} sx={{ p: 0.25 }}>
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>

        <TableCell sx={{ py: 0.5, px: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem', wordBreak: 'break-word' }}>
            {item.itemName || item.dataSourceItemGroup || `Item #${item.id}`}
          </Typography>
          {item.mimeType && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
              {item.mimeType}
            </Typography>
          )}
        </TableCell>

        <TableCell sx={{ py: 0.5, px: 1 }}>
          <Chip
            icon={<StorageIcon sx={{ fontSize: '13px !important' }} />}
            label={item.dataSourceName || 'Data Source'}
            size="small"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.68rem', bgcolor: '#f8fafc' }}
          />
        </TableCell>

        <TableCell sx={{ py: 0.5, px: 1, maxWidth: 160, minWidth: 80 }}>
          {item.status === 'AWAITING_APPROVAL' ? (
            <Chip
              icon={<GavelIcon sx={{ fontSize: '13px !important', color: '#b45309' }} />}
              label="AWAITING APPROVAL"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.66rem',
                fontWeight: 700,
                bgcolor: '#fef3c7',
                color: '#b45309',
                border: '1px solid #f59e0b',
                cursor: 'pointer',
              }}
              onClick={() => setOpen(true)}
            />
          ) : (
            <Tooltip title={item.error || (isFailed ? 'Failed' : 'Success')} placement="top" arrow>
              <Chip
                icon={
                  isFailed ? (
                    <ErrorIcon sx={{ fontSize: '13px !important' }} />
                  ) : item.status === 'PROCESSED' ? (
                    <CheckCircleIcon sx={{ fontSize: '13px !important' }} />
                  ) : (
                    <HourglassEmptyIcon sx={{ fontSize: '13px !important' }} />
                  )
                }
                label={isFailed ? 'FAILED' : item.status || 'UNPROCESSED'}
                size="small"
                color={isFailed ? 'error' : item.status === 'PROCESSED' ? 'success' : 'default'}
                variant="filled"
                sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, cursor: item.error ? 'help' : 'default' }}
              />
            </Tooltip>
          )}
          {item.error && (
            <Tooltip title={item.error} placement="bottom" arrow>
              <Typography
                variant="caption"
                color="error"
                sx={{
                  display: 'block',
                  fontSize: '0.65rem',
                  mt: 0.25,
                  maxWidth: 150,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  cursor: 'help',
                }}
              >
                {item.error}
              </Typography>
            </Tooltip>
          )}
        </TableCell>

        <TableCell sx={{ py: 0.5, px: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
            {item.lastProcessedStartDate ? new Date(item.lastProcessedStartDate).toLocaleTimeString() : '-'}
          </Typography>
          {duration && (
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
              ⏱ {duration}
            </Typography>
          )}
        </TableCell>

        <TableCell sx={{ py: 0.5, px: 1 }} align="right">
          <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
            {item.status === 'AWAITING_APPROVAL' && pipelineInstanceId && (
              <>
                <Tooltip title="Approve & Resume this item">
                  <IconButton
                    size="small"
                    disabled={isResumingItem}
                    onClick={async (e) => {
                      e.stopPropagation();
                      setIsResumingItem(true);
                      try {
                        await apiContext.resumePipelineStep(pipelineInstanceId, step.entityId || step.id, {
                          approved: true,
                          dataSourceItemId: item.dataSourceItemId,
                          itemProcessingStatusId: item.id,
                          entityDefId: step.entityDefId,
                        });
                        onChange?.();
                      } catch (err) {
                        console.error('Failed to approve item:', err);
                      } finally {
                        setIsResumingItem(false);
                      }
                    }}
                    sx={{ p: 0.5, color: '#16a34a', bgcolor: '#f0fdf4', '&:hover': { bgcolor: '#dcfce7' } }}
                  >
                    <CheckCircleIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reject this item">
                  <IconButton
                    size="small"
                    disabled={isResumingItem}
                    onClick={async (e) => {
                      e.stopPropagation();
                      setIsResumingItem(true);
                      try {
                        await apiContext.resumePipelineStep(pipelineInstanceId, step.entityId || step.id, {
                          approved: false,
                          dataSourceItemId: item.dataSourceItemId,
                          itemProcessingStatusId: item.id,
                          entityDefId: step.entityDefId,
                        });
                        onChange?.();
                      } catch (err) {
                        console.error('Failed to reject item:', err);
                      } finally {
                        setIsResumingItem(false);
                      }
                    }}
                    sx={{ p: 0.5, color: '#dc2626', bgcolor: '#fef2f2', '&:hover': { bgcolor: '#fee2e2' } }}
                  >
                    <CancelIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {onRestartFromPoint && (
              <Tooltip title="Rerun pipeline from this item">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRestartFromPoint(step, item.id);
                  }}
                  sx={{ p: 0.5, color: '#0284c7', bgcolor: '#f0f9ff', '&:hover': { bgcolor: '#e0f2fe' } }}
                >
                  <ReplayIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
            {pipelineInstanceId && (
              <Tooltip title="Delete this item and its event data from pipeline">
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteItem();
                  }}
                  sx={{ p: 0.5, bgcolor: '#fff5f5', '&:hover': { bgcolor: '#fee2e2' } }}
                >
                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>

      {/* Expandable Events History Drawer */}
      <TableRow>
        <TableCell
          style={{ paddingBottom: 0, paddingTop: 0, maxWidth: 0 }}
          colSpan={6}
          sx={{ p: 0, width: '100%', overflow: 'hidden' }}
        >
          <Collapse in={open} timeout="auto" unmountOnExit sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
            <Box
              sx={{
                my: 1,
                p: 1.5,
                bgcolor: '#f8fafc',
                borderRadius: 1,
                border: '1px solid #e2e8f0',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
            >
              {/* Event Data List */}
              <Stack spacing={1} sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
                {deleteItemError && (
                  <Box sx={{ mb: 0.5 }}>
                    <ErrorMessage error={{ message: deleteItemError }} />
                  </Box>
                )}
                {deleteError && (
                  <Box sx={{ mb: 0.5 }}>
                    <ErrorMessage error={{ message: deleteError }} />
                  </Box>
                )}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%', minWidth: 0 }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#334155' }}>
                    Agent Execution & Output ({eventsData.length} Event{eventsData.length === 1 ? '' : 's'})
                  </Typography>
                </Stack>

                {loadingEvents ? (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                      Loading event data payloads...
                    </Typography>
                  </Stack>
                ) : eventsData.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#fff', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      No event records generated yet for this item.
                    </Typography>
                  </Paper>
                ) : (
                  eventsData.map((ev, evIdx) => (
                    <AgentEventCard key={ev.id ?? evIdx} ev={ev} onDelete={handleDeleteEvent} />
                  ))
                )}
              </Stack>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

/**
 * Module-level caches to preserve items, execution timing, and progress state across step navigation
 */
const agentItemsCache = new Map<string, ItemProcessingStatusDto[]>();
const stepExecutionStartTimes = new Map<string, number>();

/**
 * Main AgentStepItemsView component
 */
export const AgentStepItemsView: React.FC<AgentStepItemsViewProps> = ({
  step,
  pipelineExecutionState,
  onRestartFromPoint,
  onExecuteStep,
  suppressEvents,
  onToggleSuppressEvents,
  stepExecutionResponse,
  onClearStepExecutionResponse,
  onChange,
  isExecuting,
}) => {
  const apiContext = useApiContext();

  const stepKey = `${step?.entityDefId || ''}_${step?.entityId || step?.id}_${pipelineExecutionState?.pipelineInstanceId || ''}`;

  const [rawItems, setRawItems] = useState<ItemProcessingStatusDto[]>(() => agentItemsCache.get(stepKey) || []);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Live progress monitoring states
  const [executionStartTime, setExecutionStartTime] = useState<number | null>(() => stepExecutionStartTimes.get(stepKey) || null);
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(() => {
    const cachedStart = stepExecutionStartTimes.get(stepKey);
    return cachedStart ? Math.max(0, Math.floor((Date.now() - cachedStart) / 1000)) : 0;
  });
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [isBackgroundProcessing, setIsBackgroundProcessing] = useState<boolean>(false);

  const [selectedDataSourceId, setSelectedDataSourceId] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'success' | 'failed'>('all');

  // Identify data sources that are part of this pipeline (matching both defId and instanceId)
  const pipelineDataSources = useMemo(() => {
    return pipelineExecutionState?.steps?.filter((s) => s.entityType === EntityType.dataSourceInstance) || [];
  }, [pipelineExecutionState]);

  // Fetch agent item processing statuses
  const fetchItems = async (showLoading = true) => {
    if (showLoading && !agentItemsCache.has(stepKey)) setLoading(true);
    setFetchError(null);
    try {
      const data: ItemProcessingStatusDto[] = await apiContext.getAgentInstanceItemProcessingStatuses(
        step.entityDefId,
        step.entityId,
        pipelineExecutionState?.pipelineInstanceId,
      );
      agentItemsCache.set(stepKey, data || []);
      setRawItems(data || []);
    } catch (err: any) {
      console.error('Error fetching agent items:', err);
      if (showLoading) {
        setFetchError(err?.response?.data?.message || err?.message || 'Failed to fetch items for this agent.');
      }
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    if (step && (step.entityDefId || step.entityId)) {
      fetchItems(true);
    }
  }, [step?.entityDefId, step?.entityId, pipelineExecutionState?.id, stepExecutionResponse]);

  // Filter items to only those belonging to the current pipeline's data sources
  const pipelineItems = useMemo(() => {
    if (pipelineDataSources.length === 0) return rawItems;
    return rawItems.filter((item) =>
      pipelineDataSources.some(
        (ds) => ds.entityDefId === item.dataSourceDefId && ds.entityId === item.dataSourceInstanceId,
      ),
    );
  }, [rawItems, pipelineDataSources]);

  // Compute upstream total items count
  const upstreamTotalItems = useMemo(() => {
    if (!pipelineExecutionState?.steps) return 0;
    const upstreamEntities = step.upstreamEntities || [];
    const upstreamSteps = pipelineExecutionState.steps.filter((s) =>
      upstreamEntities.some(
        (u) =>
          u.entityId === s.entityId ||
          u.entityName === s.name ||
          u.entityName === s.entityName,
      ),
    );
    let count = 0;
    for (const upStep of upstreamSteps) {
      if (upStep.lastRun?.totalItems) {
        count = Math.max(count, upStep.lastRun.totalItems);
      } else if (upStep.lastRun?.successCount) {
        count = Math.max(count, upStep.lastRun.successCount);
      }
    }
    return count;
  }, [pipelineExecutionState?.steps, step.upstreamEntities]);

  const isStepRunning = Boolean(
    isExecuting ||
    isBackgroundProcessing ||
    step.status === 'running' ||
    (step.status as string) === 'processing',
  );
  // Total expected count is always derived from upstream data sources or historical items
  const totalCount = Math.max(pipelineItems.length, upstreamTotalItems, step.lastRun?.totalItems || 0);

  // Compute items processed during the active execution run (since executionStartTime)
  const currentRunProcessedCount = useMemo(() => {
    if (!isStepRunning || !executionStartTime) return 0;
    return pipelineItems.filter((it) => {
      const itemTime = it.lastProcessedEndDate
        ? new Date(it.lastProcessedEndDate).getTime()
        : it.lastProcessedStartDate
          ? new Date(it.lastProcessedStartDate).getTime()
          : 0;
      return itemTime >= executionStartTime - 4000 && it.status?.toUpperCase() === 'PROCESSED';
    }).length;
  }, [pipelineItems, isStepRunning, executionStartTime]);

  const currentRunFailedCount = useMemo(() => {
    if (!isStepRunning || !executionStartTime) return 0;
    return pipelineItems.filter((it) => {
      const itemTime = it.lastProcessedEndDate
        ? new Date(it.lastProcessedEndDate).getTime()
        : it.lastProcessedStartDate
          ? new Date(it.lastProcessedStartDate).getTime()
          : 0;
      const isFail =
        it.status?.toLowerCase() === 'failed' ||
        it.status?.toLowerCase() === 'error' ||
        Boolean(it.error) ||
        Boolean(it.dsError);
      return itemTime >= executionStartTime - 4000 && isFail;
    }).length;
  }, [pipelineItems, isStepRunning, executionStartTime]);

  const inProgressCount = useMemo(
    () => pipelineItems.filter((it) => it.status?.toUpperCase() === 'PROCESSING').length,
    [pipelineItems],
  );

  // Overall counts when not running, or run-specific counts when running
  const processedCount = isStepRunning
    ? currentRunProcessedCount
    : pipelineItems.filter((it) => it.status?.toUpperCase() === 'PROCESSED').length;
  const failedCount = isStepRunning
    ? currentRunFailedCount
    : pipelineItems.filter(
        (it) =>
          it.status?.toLowerCase() === 'failed' ||
          it.status?.toLowerCase() === 'error' ||
          Boolean(it.error) ||
          Boolean(it.dsError),
      ).length;

  const remainingCount = Math.max(
    0,
    totalCount - processedCount - failedCount - (isStepRunning ? inProgressCount : 0),
  );

  const progressPercent =
    totalCount > 0
      ? isStepRunning
        ? Math.round(((processedCount + failedCount) / totalCount) * 100)
        : Math.round((processedCount / totalCount) * 100)
      : 0;

  // Manage execution start/stop and elapsed timer
  useEffect(() => {
    let timer: any = null;
    if (isStepRunning) {
      let startTime = stepExecutionStartTimes.get(stepKey);
      if (!startTime) {
        const stepStart = step.lastRun?.startTime ? new Date(step.lastRun.startTime).getTime() : 0;
        const now = Date.now();
        startTime = stepStart > 0 && now - stepStart < 1800000 ? stepStart : now;
        stepExecutionStartTimes.set(stepKey, startTime);
      }
      setExecutionStartTime(startTime);
      setElapsedSeconds(Math.max(0, Math.floor((Date.now() - startTime) / 1000)));

      timer = setInterval(() => {
        setElapsedSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      stepExecutionStartTimes.delete(stepKey);
      setExecutionStartTime(null);
      setElapsedSeconds(0);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [isStepRunning, stepKey, step.lastRun?.startTime]);

  // Adaptive polling interval when executing or processing in background
  useEffect(() => {
    let pollInterval: any = null;
    const shouldPoll = autoRefresh && (isExecuting || isBackgroundProcessing || inProgressCount > 0);

    if (shouldPoll) {
      pollInterval = setInterval(async () => {
        await fetchItems(false);
      }, 2500);
    }

    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [autoRefresh, isExecuting, isBackgroundProcessing, inProgressCount, step?.entityDefId, step?.entityId, pipelineExecutionState?.pipelineInstanceId]);

  // Auto-detect when background processing reaches completion
  useEffect(() => {
    if (isBackgroundProcessing) {
      if (totalCount > 0 && (currentRunProcessedCount + currentRunFailedCount) >= totalCount) {
        setIsBackgroundProcessing(false);
        if (onChange) onChange();
        if (onClearStepExecutionResponse) onClearStepExecutionResponse();
      }
    }
  }, [isBackgroundProcessing, totalCount, currentRunProcessedCount, currentRunFailedCount, onChange, onClearStepExecutionResponse]);

  // Detect execution start, background processing, or timeout from stepExecutionResponse
  useEffect(() => {
    if (stepExecutionResponse) {
      const err = (stepExecutionResponse.error || '').toLowerCase();
      if (stepExecutionResponse.status === 'running' || err.includes('timeout') || err.includes('timed out')) {
        setIsBackgroundProcessing(true);
        setAutoRefresh(true);
      } else if (stepExecutionResponse.status === 'completed') {
        setIsBackgroundProcessing(false);
        fetchItems(false);
      }
    } else {
      if (!isExecuting && step?.status !== 'running' && (step?.status as string) !== 'processing') {
        setIsBackgroundProcessing(false);
      }
    }
  }, [stepExecutionResponse, isExecuting, step?.status]);

  // Apply user toolbar filters: Selected Data Source, Search, and Status
  const filteredItems = useMemo(() => {
    return pipelineItems.filter((item) => {
      // Data Source filter
      if (selectedDataSourceId !== 'all' && item.dataSourceInstanceId !== selectedDataSourceId) {
        return false;
      }

      // Status filter
      const isFailed =
        item.status?.toLowerCase() === 'failed' ||
        item.status?.toLowerCase() === 'error' ||
        Boolean(item.error) ||
        Boolean(item.dsError);

      if (statusFilter === 'success' && isFailed) return false;
      if (statusFilter === 'failed' && !isFailed) return false;

      // Search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const nameMatch = item.itemName?.toLowerCase().includes(query);
        const dsMatch = item.dataSourceName?.toLowerCase().includes(query);
        const mimeMatch = item.mimeType?.toLowerCase().includes(query);
        const errorMatch = item.error?.toLowerCase().includes(query);
        const idMatch = String(item.dataSourceItemId || item.id)
          .toLowerCase()
          .includes(query);
        if (!nameMatch && !dsMatch && !mimeMatch && !errorMatch && !idMatch) return false;
      }

      return true;
    });
  }, [pipelineItems, selectedDataSourceId, statusFilter, searchQuery]);

  if (fetchError) {
    return (
      <Box sx={{ p: 2 }}>
        <ErrorMessage error={fetchError} />
      </Box>
    );
  }

  if (!loading && !isExecuting && !isBackgroundProcessing && rawItems.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#f8fafc',
            borderRadius: 2,
            border: '1px dashed #cbd5e1',
            my: 1,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderRadius: '50%',
              bgcolor: '#eff6ff',
              color: '#2563eb',
              mb: 2,
              display: 'flex',
            }}
          >
            <SmartToyIcon sx={{ fontSize: 36 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 0.5 }}>
            No items processed yet
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 460, textAlign: 'center', mb: 2 }}>
            This agent processes items sequentially as they are ingested by upstream data sources in this pipeline.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={() => fetchItems(true)}
            sx={{ textTransform: 'none', fontSize: '0.78rem' }}
          >
            Check Status / Refresh
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        flexGrow: 1,
        overflow: 'hidden',
        gap: 1,
      }}
    >
      {/* Live Progress Monitoring Banner - only shown when agent is actively running */}
      {isStepRunning && (
        <Paper
          elevation={0}
          sx={{
            p: 1.25,
            borderRadius: 1.5,
            bgcolor: isBackgroundProcessing ? '#fffbeb' : '#f0f9ff',
            border: isBackgroundProcessing ? '1px solid #fde68a' : '1px solid #bae6fd',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            flexShrink: 0,
          }}
        >
          {/* Row 1: Header with status chip, message, elapsed time, controls */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, flex: 1 }}>
              {isStepRunning ? (
                <Chip
                  icon={<CircularProgress size={12} color="inherit" />}
                  label={isBackgroundProcessing ? 'PROCESSING IN BACKGROUND' : 'RUNNING'}
                  size="small"
                  color={isBackgroundProcessing ? 'warning' : 'primary'}
                  sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, px: 0.25 }}
                />
              ) : remainingCount === 0 && failedCount === 0 && totalCount > 0 ? (
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: 13 }} />}
                  label="COMPLETED"
                  size="small"
                  color="success"
                  sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700 }}
                />
              ) : failedCount > 0 && remainingCount === 0 ? (
                <Chip
                  icon={<ErrorIcon sx={{ fontSize: 13 }} />}
                  label="COMPLETED WITH ERRORS"
                  size="small"
                  color="error"
                  sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700 }}
                />
              ) : (
                <Chip
                  icon={<HourglassEmptyIcon sx={{ fontSize: 13 }} />}
                  label="PARTIALLY PROCESSED"
                  size="small"
                  sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700 }}
                />
              )}

              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem', color: '#1e293b' }} noWrap>
                {isBackgroundProcessing
                  ? 'Request timed out waiting for batch, but execution is active in background'
                  : isStepRunning
                    ? 'Processing pipeline items and emitting events...'
                    : remainingCount === 0
                      ? failedCount > 0
                        ? `Execution finished: ${processedCount} succeeded, ${failedCount} failed`
                        : 'All pipeline items processed!'
                      : 'Step execution in progress'}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
              {(isExecuting || isBackgroundProcessing || elapsedSeconds > 0) && (
                <Typography
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    color: '#475569',
                    bgcolor: 'rgba(255,255,255,0.8)',
                    px: 0.75,
                    py: 0.2,
                    borderRadius: 1,
                    border: '1px solid #e2e8f0',
                  }}
                >
                  ⏱ {Math.floor(elapsedSeconds / 60).toString().padStart(2, '0')}:{(elapsedSeconds % 60).toString().padStart(2, '0')}
                </Typography>
              )}

              <Tooltip title={autoRefresh ? 'Click to pause live polling' : 'Click to enable live auto-refresh'}>
                <Button
                  size="small"
                  variant={autoRefresh ? 'contained' : 'outlined'}
                  color={autoRefresh ? 'primary' : 'inherit'}
                  onClick={() => setAutoRefresh(!autoRefresh)}
                  startIcon={
                    <SyncIcon
                      sx={{
                        fontSize: 13,
                        animation: loading ? 'spin 1.5s linear infinite' : 'none',
                        '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
                      }}
                    />
                  }
                  sx={{
                    height: 22,
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 0.75,
                    boxShadow: 'none',
                  }}
                >
                  Live Monitor: {autoRefresh ? 'ON' : 'OFF'}
                </Button>
              </Tooltip>

              <Tooltip title="Refresh item statuses now">
                <IconButton
                  size="small"
                  onClick={() => fetchItems(false)}
                  disabled={loading}
                  sx={{ p: 0.25, bgcolor: '#ffffff', border: '1px solid #cbd5e1' }}
                >
                  <RefreshIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>

              <Tooltip title="Dismiss banner">
                <IconButton
                  size="small"
                  onClick={() => {
                    setIsBackgroundProcessing(false);
                    if (onClearStepExecutionResponse) onClearStepExecutionResponse();
                  }}
                  sx={{ p: 0.25, bgcolor: '#ffffff', border: '1px solid #cbd5e1' }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* Row 2: Progress Bar */}
          <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem', color: '#334155' }}>
                {isStepRunning
                  ? `Progress: ${processedCount + failedCount} of ${totalCount} items (${progressPercent}%)`
                  : failedCount > 0
                    ? `Completed: ${processedCount} of ${totalCount} items (${progressPercent}%) • ${failedCount} failed`
                    : `Progress: ${processedCount} of ${totalCount} items (100%)`}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem', color: '#64748b' }}>
                {isStepRunning
                  ? `${remainingCount} queued / remaining`
                  : remainingCount > 0
                    ? `${remainingCount} unprocessed`
                    : 'All pipeline items processed'}
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              color={
                isStepRunning
                  ? isBackgroundProcessing
                    ? 'warning'
                    : 'primary'
                  : failedCount > 0 && processedCount === 0
                    ? 'error'
                    : failedCount > 0
                      ? 'warning'
                      : 'success'
              }
              sx={{
                height: 7,
                borderRadius: 1,
                bgcolor: 'rgba(0,0,0,0.06)',
                '& .MuiLinearProgress-bar': { borderRadius: 1 },
              }}
            />
          </Box>

          {/* Row 3: Metrics breakdown badges */}
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
            <Chip
              label={`Total: ${totalCount}`}
              size="small"
              variant="outlined"
              sx={{ height: 19, fontSize: '0.66rem', fontWeight: 600, bgcolor: '#ffffff' }}
            />
            <Chip
              label={isStepRunning ? `Processed: ${processedCount}` : `Success: ${processedCount}`}
              size="small"
              color={isStepRunning ? 'primary' : 'success'}
              variant="filled"
              sx={{ height: 19, fontSize: '0.66rem', fontWeight: 700 }}
            />
            {failedCount > 0 && (
              <Chip
                label={`Failed: ${failedCount}`}
                size="small"
                color="error"
                variant="filled"
                sx={{ height: 19, fontSize: '0.66rem', fontWeight: 700 }}
              />
            )}
            {inProgressCount > 0 && (
              <Chip
                label={`In-Progress: ${inProgressCount}`}
                size="small"
                color="info"
                variant="filled"
                sx={{ height: 19, fontSize: '0.66rem', fontWeight: 700 }}
              />
            )}
            {remainingCount > 0 && (
              <Chip
                label={`Queued: ${remainingCount}`}
                size="small"
                variant="outlined"
                sx={{ height: 19, fontSize: '0.66rem', fontWeight: 600, bgcolor: '#ffffff' }}
              />
            )}
          </Stack>
        </Paper>
      )}

      {/* Top Controls Toolbar */}
      <Paper variant="outlined" sx={{ p: 0.75, bgcolor: '#f8fafc', borderRadius: 1, flexShrink: 0 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
          {/* Left: Pipeline Scope Badge & Optional Data Source Filter */}
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Chip
              icon={<SmartToyIcon sx={{ fontSize: 14 }} />}
              label={`${filteredItems.length} Processed Item${filteredItems.length === 1 ? '' : 's'}`}
              size="small"
              sx={{ height: 26, fontSize: '0.75rem', bgcolor: '#e0f2fe', color: '#0369a1', fontWeight: 600 }}
            />

            {pipelineDataSources.length > 1 && (
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={selectedDataSourceId}
                  onChange={(e) => setSelectedDataSourceId(e.target.value)}
                  displayEmpty
                  sx={{ height: 26, fontSize: '0.72rem', bgcolor: '#fff' }}
                >
                  <MenuItem value="all" sx={{ fontSize: '0.75rem' }}>
                    All Data Sources ({pipelineItems.length})
                  </MenuItem>
                  {pipelineDataSources.map((ds) => {
                    const dsCount = rawItems.filter(
                      (r) => r.dataSourceDefId === ds.entityDefId && r.dataSourceInstanceId === ds.entityId,
                    ).length;
                    return (
                      <MenuItem key={ds.id} value={ds.entityId} sx={{ fontSize: '0.75rem' }}>
                        {ds.name || ds.entityName || 'Data Source'} ({dsCount})
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            )}
          </Stack>

          {/* Right: Search & Status Filters, Live Monitor Toggle, Refresh */}
          <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                sx: { height: 26, fontSize: '0.72rem', width: 140 },
              }}
            />

            <Stack direction="row" spacing={0.25}>
              <Chip
                label="All"
                size="small"
                color={statusFilter === 'all' ? 'primary' : 'default'}
                variant={statusFilter === 'all' ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter('all')}
                sx={{ height: 20, fontSize: '0.68rem', cursor: 'pointer', px: 0.5 }}
              />
              <Chip
                label="Success"
                size="small"
                color={statusFilter === 'success' ? 'success' : 'default'}
                variant={statusFilter === 'success' ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter('success')}
                sx={{ height: 20, fontSize: '0.68rem', cursor: 'pointer', px: 0.5 }}
              />
              <Chip
                label="Failed"
                size="small"
                color={statusFilter === 'failed' ? 'error' : 'default'}
                variant={statusFilter === 'failed' ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter('failed')}
                sx={{ height: 20, fontSize: '0.68rem', cursor: 'pointer', px: 0.5 }}
              />
            </Stack>

            {!(isStepRunning || totalCount > 0) && (
              <>
                <Tooltip title={autoRefresh ? 'Live Auto-Refresh active (polling every 2.5s). Click to pause.' : 'Live Auto-Refresh paused. Click to enable.'}>
                  <Button
                    size="small"
                    variant={autoRefresh ? 'contained' : 'outlined'}
                    color={autoRefresh ? 'primary' : 'inherit'}
                    onClick={() => setAutoRefresh(!autoRefresh)}
                    startIcon={
                      <SyncIcon
                        sx={{
                          fontSize: 13,
                          animation: loading ? 'spin 1.5s linear infinite' : 'none',
                          '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } },
                        }}
                      />
                    }
                    sx={{
                      height: 24,
                      fontSize: '0.68rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      px: 0.75,
                      boxShadow: 'none',
                    }}
                  >
                    Live Monitor: {autoRefresh ? 'ON' : 'OFF'}
                  </Button>
                </Tooltip>

                <Tooltip title="Refresh item statuses now">
                  <IconButton
                    size="small"
                    onClick={() => fetchItems(false)}
                    disabled={loading}
                    sx={{ p: 0.25, bgcolor: '#ffffff', border: '1px solid #cbd5e1' }}
                  >
                    <RefreshIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Stack>
        </Stack>
      </Paper>

      {/* Items Table */}
      {loading ? (
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
          <Typography variant="body2" color="text.secondary">
            Loading agent processed items...
          </Typography>
        </Stack>
      ) : filteredItems.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 1 }}>
          <Typography color="text.secondary" variant="body2">
            {pipelineItems.length === 0
              ? 'No items processed by this agent for this pipeline.'
              : 'No items match the current search or status filter.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 36, bgcolor: '#f8fafc', py: 0.5 }} />
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 0.5, fontSize: '0.75rem', width: '30%' }}>
                  Item Name
                </TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 0.5, fontSize: '0.75rem', width: '25%' }}>
                  Data Source
                </TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 0.5, fontSize: '0.75rem', width: 105 }}>
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 600, bgcolor: '#f8fafc', py: 0.5, fontSize: '0.75rem', width: 120 }}>
                  Processed Timing
                </TableCell>
                <TableCell sx={{ width: 75, bgcolor: '#f8fafc', py: 0.5 }} align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredItems.map((item, idx) => (
                <AgentStepItemRow
                  key={
                    item.id ?? `${item.dataSourceDefId}-${item.dataSourceInstanceId}-${item.dataSourceItemId}-${idx}`
                  }
                  step={step}
                  item={item}
                  pipelineInstanceId={pipelineExecutionState?.pipelineInstanceId || pipelineExecutionState?.id}
                  onRestartFromPoint={onRestartFromPoint}
                  onItemDeleted={fetchItems}
                  onChange={onChange}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AgentStepItemsView;
